import React, { useEffect, useRef } from "react";
import { useTheme } from "../hooks/useTheme";

const CanvasBreathingLight = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const animate = () => {
      time += 0.01;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Create breathing light pattern
      const breathingIntensity = Math.sin(time) * 0.3 + 0.7; // Oscillates between 0.4 and 1.0
      const shimmerTime = time * 1.5;

      // Multiple light sources with different phases
      const lightSources = [
        {
          x: canvas.width * 0.2,
          y: canvas.height * 0.3,
          phase: 0,
          intensity: 0.6,
        },
        {
          x: canvas.width * 0.7,
          y: canvas.height * 0.6,
          phase: Math.PI / 3,
          intensity: 0.8,
        },
        {
          x: canvas.width * 0.5,
          y: canvas.height * 0.1,
          phase: Math.PI / 2,
          intensity: 0.4,
        },
        {
          x: canvas.width * 0.9,
          y: canvas.height * 0.8,
          phase: Math.PI,
          intensity: 0.5,
        },
        {
          x: canvas.width * 0.1,
          y: canvas.height * 0.9,
          phase: Math.PI * 1.5,
          intensity: 0.7,
        },
      ];

      lightSources.forEach((light) => {
        const lightBreathing = Math.sin(shimmerTime + light.phase) * 0.4 + 0.6;
        const finalIntensity =
          breathingIntensity * lightBreathing * light.intensity;

        // Create radial gradient for each light source
        const gradient = ctx.createRadialGradient(
          light.x,
          light.y,
          0,
          light.x,
          light.y,
          Math.max(canvas.width, canvas.height) * 0.8
        );

        if (isDarkMode) {
          gradient.addColorStop(
            0,
            `rgba(255, 255, 255, ${finalIntensity * 0.08})`
          );
          gradient.addColorStop(
            0.3,
            `rgba(200, 200, 200, ${finalIntensity * 0.04})`
          );
          gradient.addColorStop(
            0.6,
            `rgba(150, 150, 150, ${finalIntensity * 0.02})`
          );
          gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
        } else {
          gradient.addColorStop(0, `rgba(0, 0, 0, ${finalIntensity * 0.06})`);
          gradient.addColorStop(
            0.3,
            `rgba(50, 50, 50, ${finalIntensity * 0.03})`
          );
          gradient.addColorStop(
            0.6,
            `rgba(100, 100, 100, ${finalIntensity * 0.015})`
          );
          gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        }

        ctx.fillStyle = gradient;
        ctx.globalCompositeOperation = "screen";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      });

      // Add subtle shimmer overlay
      const shimmerGradient = ctx.createLinearGradient(
        0,
        0,
        canvas.width,
        canvas.height
      );
      const shimmerOpacity =
        (Math.sin(time * 2) * 0.1 + 0.1) * breathingIntensity;

      if (isDarkMode) {
        shimmerGradient.addColorStop(0, "rgba(255, 255, 255, 0)");
        shimmerGradient.addColorStop(
          0.5,
          `rgba(255, 255, 255, ${shimmerOpacity * 0.02})`
        );
        shimmerGradient.addColorStop(1, "rgba(255, 255, 255, 0)");
      } else {
        shimmerGradient.addColorStop(0, "rgba(0, 0, 0, 0)");
        shimmerGradient.addColorStop(
          0.5,
          `rgba(0, 0, 0, ${shimmerOpacity * 0.015})`
        );
        shimmerGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      }

      ctx.fillStyle = shimmerGradient;
      ctx.globalCompositeOperation = "multiply";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      animationRef.current = requestAnimationFrame(animate);
    };

    resizeCanvas();
    animate();

    window.addEventListener("resize", resizeCanvas);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ mixBlendMode: isDarkMode ? "screen" : "multiply" }}
    />
  );
};

export default CanvasBreathingLight;
